import {
  CABINET_DIALOG,
  CABINET_PATHS,
  CABINET_PLATFORM,
} from '@nebalkon-workspace/cabinet-core';
import { Dialog } from '@angular/cdk/dialog';
import { StaffUserItemDetailModalComponent } from '../admin/staff-user-item-detail-modal.component';
import { StaffQrScanFlowService } from './staff-qr-scan-flow.service';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AcceptanceActDocxService,
  downloadAcceptanceActForOrderIntake,
  isClientProfileVerified,
  resolveIntakeItemsForReceive,
  type OrderReceiveParsed,
} from '@nebalkon-workspace/cabinet-client';
import { AuthService, ProfileService, UserRole } from '@nebalkon-workspace/auth';
import type { ProfileWithUserResponse } from '@nebalkon-workspace/auth';
import { YmParamsEnum, YmReachGoal } from '@nebalkon-workspace/common';
import { YMService } from '@grandgular/yandex-metrika';
import { type AdminUserItemResponse, UserAdminService } from '@nebalkon-workspace/admin-api';
import { OrderApiService, OrderResponse } from '@nebalkon-workspace/order-api';
import {
  BoxResponse,
  BoxService,
  ItemResponse,
  ItemService,
  StorageStatus,
  WarehouseService,
  type UpdateStaffItemRequest,
} from '@nebalkon-workspace/storage-api';
import { NotButton, NotInput, NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { FormsModule } from '@angular/forms';
import { forkJoin, of, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { warehouseRouteSegment } from '../utils/warehouse-route.util';

@Component({
  selector: 'lib-staff-qr-scan',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, FormsModule, NotInput, NotButton],
  templateUrl: './staff-qr-scan.component.html',
  styleUrl: './staff-qr-scan.component.scss',
})
export class StaffQrScanComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #dialog = inject(CABINET_DIALOG);
  readonly #modalDialog = inject(Dialog);
  readonly #qrScanFlow = inject(StaffQrScanFlowService);
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #platform = inject(CABINET_PLATFORM);
  protected readonly isWebPlatform = this.#platform === 'web';
  protected readonly manualCode = signal('');
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);
  readonly #auth = inject(AuthService);
  readonly #profile = inject(ProfileService);
  readonly #userAdmin = inject(UserAdminService);
  readonly #boxService = inject(BoxService);
  readonly #itemService = inject(ItemService);
  readonly #orders = inject(OrderApiService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #acceptanceAct = inject(AcceptanceActDocxService);
  readonly #warehouseService = inject(WarehouseService);
  readonly #ym = inject(YMService, { optional: true });

  protected readonly clientProfile = signal<ProfileWithUserResponse | null>(null);
  protected readonly clientProfileLoading = signal(false);

  #staffQrUnsupportedTracked = false;

  protected readonly loading = signal(true);
  protected readonly receiving = signal(false);
  protected readonly itemDetailLoading = signal(false);
  protected readonly errorKey = signal<string | null>(null);

  protected readonly kind = signal<'box' | 'item' | null>(null);
  protected readonly box = signal<BoxResponse | null>(null);
  protected readonly parentBox = signal<BoxResponse | null>(null);
  protected readonly item = signal<ItemResponse | null>(null);
  protected readonly boxItems = signal<ItemResponse[]>([]);
  protected readonly order = signal<OrderResponse | null>(null);

  protected readonly primaryBox = computed<BoxResponse | null>(() => {
    if (this.kind() === 'box') {
      return this.box();
    }
    return this.parentBox();
  });

  protected readonly isWarehouseWorker = computed(() =>
    this.#auth.roles().includes(UserRole.WarehouseWorker),
  );

  protected readonly canWarehouseMove = computed(() => {
    if (!this.isWarehouseWorker()) {
      return false;
    }
    const b = this.primaryBox();
    if (!b || b.status !== StorageStatus.Stored) {
      return false;
    }
    return !!(b.warehouse_id || this.order()?.warehouse_id);
  });

  protected readonly canWarehouseClear = computed(() => {
    if (!this.isWarehouseWorker()) {
      return false;
    }
    const b = this.primaryBox();
    if (!b || b.status !== StorageStatus.Stored) {
      return false;
    }
    return !!(b.warehouse_id || this.order()?.warehouse_id);
  });

  protected readonly canWarehouseMoveItem = computed(() => {
    if (!this.isWarehouseWorker()) {
      return false;
    }
    const it = this.item();
    return (
      this.kind() === 'item' &&
      !!it?.is_standalone &&
      !it.box_id &&
      it.status === StorageStatus.Stored
    );
  });

  protected readonly canWarehouseClearItem = computed(() => {
    if (!this.isWarehouseWorker()) {
      return false;
    }
    const it = this.item();
    return (
      this.kind() === 'item' &&
      !!it?.is_standalone &&
      !it.box_id &&
      it.status === StorageStatus.Stored &&
      (it.cells?.length ?? 0) > 0
    );
  });

  protected readonly orderLinkId = computed(() => {
    const order = this.order();
    if (order) {
      return order.id;
    }
    if (this.kind() === 'box') {
      return this.box()?.order_id ?? null;
    }
    return this.parentBox()?.order_id ?? null;
  });

  protected readonly showSearch = signal(false);
  protected readonly canOpenItemDetail = computed(
    () => this.kind() === 'item' && !!this.item(),
  );
  protected readonly canReceiveToWarehouse = computed(() => {
    if (!this.isWarehouseWorker() || this.receiving()) {
      return false;
    }
    const ord = this.order();
    if (!ord || ord.status !== 'in_transit' || !ord.warehouse_id) {
      return false;
    }
    if (ord.user_id) {
      if (this.clientProfileLoading()) {
        return false;
      }
      if (!isClientProfileVerified(this.clientProfile())) {
        return false;
      }
    }
    if (this.kind() === 'box') {
      return !!this.box();
    }
    return !!this.item();
  });

  protected readonly receiveBlockedByUnverifiedProfile = computed(() => {
    const ord = this.order();
    if (!ord?.user_id || this.clientProfileLoading()) {
      return false;
    }
    return !isClientProfileVerified(this.clientProfile());
  });

  protected readonly showReceiveProfileBlock = computed(() => {
    const ord = this.order();
    if (
      !this.isWarehouseWorker() ||
      !ord ||
      ord.status !== 'in_transit' ||
      !ord.warehouse_id ||
      !ord.user_id
    ) {
      return false;
    }
    if (this.kind() === 'box' && !this.box()) {
      return false;
    }
    if (this.kind() === 'item' && !this.item()) {
      return false;
    }
    return this.receiveBlockedByUnverifiedProfile();
  });

  public ngOnInit(): void {
    const q = this.#route.snapshot.queryParamMap;
    const boxId = q.get('box');
    const itemId = q.get('item');
    if (boxId) {
      this.kind.set('box');
      this.#loadBox(boxId);
      return;
    }
    if (itemId) {
      this.kind.set('item');
      this.#loadItem(itemId);
      return;
    }
    this.loading.set(false);
    this.showSearch.set(true);
    this.#trackStaffQrUnsupportedOnce();
  }

  #trackStaffQrUnsupportedOnce(): void {
    if (!this.isWebPlatform || this.#staffQrUnsupportedTracked) {
      return;
    }

    this.#staffQrUnsupportedTracked = true;
    this.#ym
      ?.params({
        [YmParamsEnum.StaffQrUnsupported]: { context: 'desktop_web' },
      })
      .reachGoal(YmReachGoal.StaffQrUnsupported);
  }

  protected async submitManualCode(): Promise<void> {
    const parsed = await this.#qrScanFlow.resolveAndNavigate(this.manualCode(), {
      relativeTo: this.#route,
    });
    if (!parsed) {
      return;
    }

    if ('user_id' in parsed || 'cell_id' in parsed) {
      this.showSearch.set(false);
      this.loading.set(false);
      return;
    }

    this.showSearch.set(false);
    this.loading.set(true);

    if ('box_id' in parsed) {
      this.kind.set('box');
      this.#loadBox(parsed.box_id);
      return;
    }

    this.kind.set('item');
    this.#loadItem(parsed.item_id);
  }

  protected async scanWithCamera(): Promise<void> {
    const parsed = await this.#qrScanFlow.scanAndNavigateToResult({
      relativeTo: this.#route,
    });
    if (!parsed) {
      return;
    }

    if ('user_id' in parsed) {
      this.showSearch.set(false);
      this.loading.set(false);
      return;
    }

    if ('cell_id' in parsed) {
      this.showSearch.set(false);
      this.loading.set(false);
      return;
    }

    this.showSearch.set(false);
    this.loading.set(true);

    if ('box_id' in parsed) {
      this.kind.set('box');
      this.#loadBox(parsed.box_id);
      return;
    }

    this.kind.set('item');
    this.#loadItem(parsed.item_id);
  }

  #setOrder(order: OrderResponse | null): void {
    this.order.set(order);
    if (order?.user_id) {
      this.#loadClientProfile(order.user_id);
    } else {
      this.clientProfile.set(null);
      this.clientProfileLoading.set(false);
    }
  }

  #loadClientProfile(userId: string): void {
    this.clientProfileLoading.set(true);
    this.#userAdmin.getUserProfile(userId).subscribe({
      next: (profile) => {
        this.clientProfile.set(profile);
        this.clientProfileLoading.set(false);
      },
      error: () => {
        this.clientProfile.set(null);
        this.clientProfileLoading.set(false);
      },
    });
  }

  #loadOwnerProfileFromEntity(userId: string | undefined): void {
    const id = userId?.trim();
    if (id) {
      this.#loadClientProfile(id);
      return;
    }
    this.clientProfile.set(null);
    this.clientProfileLoading.set(false);
  }

  #loadBox(id: string): void {
    this.errorKey.set(null);
    forkJoin({
      box: this.#boxService.getAdminBox(id),
      items: this.#boxService
        .getAdminBoxItems(id)
        .pipe(catchError(() => of({ data: [] as ItemResponse[], total: 0 }))),
    }).subscribe({
      next: ({ box, items }) => {
        this.box.set(box);
        this.boxItems.set(items.data);
        const orderId = box.order_id;
        if (orderId) {
          this.#orders
            .getOrder(orderId)
            .pipe(catchError(() => of(null)))
            .subscribe((o) => {
              this.#setOrder(o);
              this.loading.set(false);
            });
        } else {
          this.#loadOwnerProfileFromEntity(box.user_id);
          this.loading.set(false);
        }
      },
      error: () => {
        // QR labels are often plain UUIDs without entity prefix; if box lookup failed,
        // fallback to item lookup with the same id.
        this.kind.set('item');
        this.#loadItem(id);
      },
    });
  }

  #loadItem(id: string): void {
    this.errorKey.set(null);
    this.#itemService.getAdminItem(id).subscribe({
      next: (it) => {
        this.item.set(it);
        if (!it.box_id) {
          this.#loadOwnerProfileFromEntity(it.user_id);
          this.loading.set(false);
          return;
        }
        forkJoin({
          box: this.#boxService.getAdminBox(it.box_id),
          items: this.#boxService
            .getAdminBoxItems(it.box_id)
            .pipe(
              catchError(() => of({ data: [] as ItemResponse[], total: 0 })),
            ),
        }).subscribe({
          next: ({ box, items }) => {
            this.parentBox.set(box);
            this.boxItems.set(items.data);
            const orderId = box.order_id;
            if (orderId) {
              this.#orders
                .getOrder(orderId)
                .pipe(catchError(() => of(null)))
                .subscribe((o) => {
                  this.#setOrder(o);
                  this.loading.set(false);
                });
            } else {
              this.#loadOwnerProfileFromEntity(box.user_id ?? it.user_id);
              this.loading.set(false);
            }
          },
          error: () => {
            this.loading.set(false);
            this.errorKey.set('admin.staffQrScan.loadError');
          },
        });
      },
      error: () => {
        this.#tryOpenClientProfile(id);
      },
    });
  }

  #tryOpenClientProfile(id: string): void {
    this.#userAdmin.getUser(id).subscribe({
      next: () => {
        this.loading.set(false);
        void this.#router.navigate([this.#cabinetPaths.staff, 'users', id]);
      },
      error: () => {
        this.#tryOpenCellPage(id);
      },
    });
  }

  #tryOpenCellPage(id: string): void {
    this.#warehouseService.getWarehouseCellContents(id).subscribe({
      next: (contents) => {
        this.loading.set(false);
        const segment = warehouseRouteSegment({
          id: contents.warehouse.id,
          slug: contents.warehouse.slug,
        });
        void this.#router.navigate([
          this.#cabinetPaths.staff,
          'warehouses',
          segment,
          'cells',
          id,
        ]);
      },
      error: () => {
        this.loading.set(false);
        this.errorKey.set('admin.staffQrScan.loadError');
      },
    });
  }

  protected storageStatusLabel(status: string): string {
    const keyMap: Record<string, string> = {
      pending: 'admin.staffQrScan.statusPending',
      in_transit_to_warehouse: 'admin.staffQrScan.statusInTransitWarehouse',
      in_transit_to_client: 'admin.staffQrScan.statusInTransitClient',
      stored: 'admin.staffQrScan.statusStored',
      delivered: 'admin.staffQrScan.statusDelivered',
    };
    const k = keyMap[status];
    return k ? this.#translate.instant(k) : status;
  }

  protected ownerLine(): string {
    const o = this.order();
    if (o) {
      const name = (o.first_name ?? '').trim();
      const phone = o.phone ?? '';
      return this.#translate.instant('admin.staffQrScan.ownerFromOrder', {
        name: name || '—',
        phone,
      });
    }
    const uid =
      this.kind() === 'box'
        ? this.box()?.user_id
        : this.item()?.user_id;
    if (!uid) {
      return '—';
    }
    return `${this.#translate.instant('admin.staffQrScan.ownerUserId')}: ${uid}`;
  }

  protected addressLabel(): string | null {
    if (this.kind() === 'item') {
      const it = this.item();
      if (it?.cells?.length) {
        return it.cells.map((c) => c.address).join(', ');
      }
      return null;
    }
    const b = this.primaryBox();
    if (b?.cells?.length) {
      return b.cells.map((c) => c.address).join(', ');
    }
    return b?.location_code ?? null;
  }

  protected openOrder(): void {
    const id = this.orderLinkId();
    if (!id) {
      return;
    }
    void this.#router.navigate([this.#cabinetPaths.staff, 'orders', id]);
  }

  protected async goMoveBox(): Promise<void> {
    const b = this.primaryBox();
    if (!b) {
      return;
    }
    const warehouseId = b.warehouse_id ?? (await this.#resolvePlacementWarehouseId());
    if (!warehouseId) {
      this.#toast('admin.staffQrScan.moveNeedsWarehouse', 'error');
      return;
    }
    void this.#router.navigate(
      [this.#cabinetPaths.staff, 'warehouses', warehouseId, 'placement'],
      { queryParams: { box: b.id } },
    );
  }

  protected async goMoveItem(): Promise<void> {
    const it = this.item();
    if (!it?.is_standalone || it.box_id) {
      return;
    }
    const warehouseId = await this.#resolvePlacementWarehouseId();
    if (!warehouseId) {
      this.#toast('admin.staffQrScan.moveNeedsWarehouse', 'error');
      return;
    }
    void this.#router.navigate(
      [this.#cabinetPaths.staff, 'warehouses', warehouseId, 'placement'],
      { queryParams: { item: it.id } },
    );
  }

  async #resolvePlacementWarehouseId(): Promise<string | null> {
    const orderWh = this.order()?.warehouse_id?.trim();
    if (orderWh) {
      return orderWh;
    }
    const boxWh = this.primaryBox()?.warehouse_id?.trim();
    if (boxWh) {
      return boxWh;
    }
    const userId = this.#profile.userInfo()?.user.id;
    if (!userId) {
      return null;
    }
    try {
      const detail = await firstValueFrom(this.#userAdmin.getUser(userId));
      return detail.staff?.warehouse_id?.trim() ?? null;
    } catch {
      return null;
    }
  }

  protected async confirmClearPlacement(): Promise<void> {
    const b = this.primaryBox();
    if (!b) {
      return;
    }
    const confirmed = await this.#dialog.confirm({
      title: this.#translate.instant('admin.staffQrScan.clearConfirmTitle'),
      message: this.#translate.instant('admin.staffQrScan.clearConfirmMessage'),
      okLabel: this.#translate.instant('common.yes'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }
    this.#boxService.clearBoxPlacement(b.id).subscribe({
      next: () => {
        this.#toast('admin.staffQrScan.clearSuccess', 'success');
        this.#boxService.getAdminBox(b.id).subscribe({
          next: (fresh) => {
            if (this.kind() === 'box') {
              this.box.set(fresh);
            } else {
              this.parentBox.set(fresh);
            }
          },
        });
      },
      error: (err: HttpErrorResponse) => {
        const body = err.error as { error?: string } | null;
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [
            body?.error ??
              this.#translate.instant('admin.staffQrScan.loadError'),
          ],
        });
      },
    });
  }

  protected async confirmClearItemPlacement(): Promise<void> {
    const it = this.item();
    if (!it) {
      return;
    }
    const confirmed = await this.#dialog.confirm({
      title: this.#translate.instant('admin.staffQrScan.clearItemConfirmTitle'),
      message: this.#translate.instant('admin.staffQrScan.clearItemConfirmMessage'),
      okLabel: this.#translate.instant('common.yes'),
      cancelLabel: this.#translate.instant('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }
    this.#itemService.clearItemPlacement(it.id).subscribe({
      next: () => {
        this.#toast('admin.staffQrScan.clearSuccess', 'success');
        this.#itemService.getAdminItem(it.id).subscribe({
          next: (fresh) => this.item.set(fresh),
        });
      },
      error: (err: HttpErrorResponse) => {
        const body = err.error as { error?: string } | null;
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [
            body?.error ??
              this.#translate.instant('admin.staffQrScan.loadError'),
          ],
        });
      },
    });
  }

  protected orderStatusLabel(status: string): string {
    const map: Record<string, string> = {
      new: 'admin.storageRequests.orderStatusNew',
      in_process: 'admin.storageRequests.orderStatusInProcess',
      pending: 'admin.storageRequests.orderStatusPending',
      in_work: 'admin.storageRequests.orderStatusInWork',
      in_transit: 'admin.storageRequests.orderStatusInTransit',
      stored: 'admin.storageRequests.orderStatusStored',
      completed: 'admin.storageRequests.filterOrderCompleted',
      canceled: 'admin.storageRequests.filterCancelled',
    };
    const k = map[status];
    return k ? this.#translate.instant(k) : status;
  }

  #toast(key: string, type: 'success' | 'error'): void {
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type,
      text: [this.#translate.instant(key)],
    });
  }

  async #resolveAdminUserItem(
    itemId: string,
    userId: string,
  ): Promise<AdminUserItemResponse | null> {
    try {
      const item = await firstValueFrom(this.#itemService.getAdminItem(itemId));
      if (item.user_id !== userId) {
        return null;
      }
      if (!item.is_standalone) {
        return {
          ...item,
          in_active_order: item.in_active_order ?? false,
          allowed_next_statuses: [],
        };
      }

      const catalog = await firstValueFrom(this.#userAdmin.getUserItems(userId));
      return catalog.data.find((catalogItem) => catalogItem.id === itemId) ?? null;
    } catch {
      return null;
    }
  }

  protected async openItemDetail(): Promise<void> {
    const it = this.item();
    if (!it || this.itemDetailLoading()) {
      return;
    }

    const itemId = it.id;
    this.itemDetailLoading.set(true);
    const adminItem = await this.#resolveAdminUserItem(itemId, it.user_id);
    this.itemDetailLoading.set(false);

    if (this.kind() !== 'item' || this.item()?.id !== itemId) {
      return;
    }
    if (!adminItem) {
      this.#toast('admin.staffQrScan.loadError', 'error');
      return;
    }

    const warehouseId = await this.#resolvePlacementWarehouseId();

    const ref = this.#modalDialog.open(StaffUserItemDetailModalComponent, {
      data: {
        item: adminItem,
        context: 'warehouse_receive',
        updating: false,
        editable: true,
        ownerLastName: this.clientProfile()?.individual?.last_name,
        warehouseId: warehouseId ?? undefined,
        onChangeStatus: async (nextStatus: string): Promise<void> => {
          await firstValueFrom(
            this.#itemService.updateItemStatus(itemId, {
              new_status: nextStatus,
              reason: this.#translate.instant(
                'admin.users.items.statusChangeReason',
              ),
            }),
          );
          const refreshed = await firstValueFrom(
            this.#itemService.getAdminItem(itemId),
          );
          this.item.set(refreshed);
        },
        onSaveItem: async (body: UpdateStaffItemRequest): Promise<void> => {
          const updated = await firstValueFrom(
            this.#itemService.updateStaffItem(itemId, body),
          );
          this.item.set(updated);
        },
        onItemRefreshed: (refreshed: AdminUserItemResponse) => {
          this.item.set(refreshed);
        },
      },
      panelClass: 'client-item-detail-modal-panel',
    });
    ref.closed.subscribe((changed) => {
      if (!changed) {
        return;
      }
      this.#itemService.getAdminItem(itemId).subscribe({
        next: (fresh) => this.item.set(fresh),
      });
    });
  }

  protected receiveToWarehouse(): void {
    const ord = this.order();
    if (!ord?.warehouse_id || this.receiveBlockedByUnverifiedProfile()) {
      return;
    }
    let payload: { box_id?: string; item_id?: string } | null = null;
    let intakeItems: ItemResponse[] = [];
    if (this.kind() === 'box') {
      const b = this.box();
      if (b) {
        payload = { box_id: b.id };
        intakeItems = this.boxItems();
      }
    } else {
      const it = this.item();
      if (it) {
        payload = it.box_id ? { box_id: it.box_id } : { item_id: it.id };
        intakeItems = it.box_id ? this.boxItems() : [it];
      }
    }
    if (!payload) {
      return;
    }
    this.receiving.set(true);
    this.#orders
      .warehouseReceive(ord.id, {
        warehouse_id: ord.warehouse_id,
        ...payload,
      })
      .subscribe({
        next: () => {
          this.receiving.set(false);
          this.#toast('admin.storageRequests.receiveSuccess', 'success');
          void this.#downloadActAfterReceive(ord, payload, intakeItems);
          if (this.kind() === 'box') {
            const id = this.box()?.id;
            if (id) {
              this.loading.set(true);
              this.#loadBox(id);
            }
            return;
          }
          const id = this.item()?.id;
          if (id) {
            this.loading.set(true);
            this.#loadItem(id);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.receiving.set(false);
          const msg =
            err.error?.code === 'PROFILE_NOT_VERIFIED'
              ? this.#translate.instant(
                  'admin.users.items.receiveProfileNotVerified',
                )
              : typeof err.error?.error === 'string'
                ? err.error.error
                : this.#translate.instant('admin.storageRequests.saveError');
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'error',
            text: [msg],
          });
        },
      });
  }

  async #downloadActAfterReceive(
    order: OrderResponse,
    payload: { box_id?: string; item_id?: string },
    intakeItems: ItemResponse[],
  ): Promise<void> {
    let items = intakeItems;
    if (items.length === 0) {
      const parsed: OrderReceiveParsed = payload.box_id
        ? { box_id: payload.box_id }
        : { item_id: payload.item_id! };
      try {
        items = await resolveIntakeItemsForReceive(
          this.#itemService,
          this.#boxService,
          parsed,
        );
      } catch {
        return;
      }
    }
    if (items.length === 0) {
      return;
    }

    try {
      await downloadAcceptanceActForOrderIntake({
        actService: this.#acceptanceAct,
        userAdmin: this.#userAdmin,
        warehouseService: this.#warehouseService,
        order,
        items,
        staffProfile: this.#profile.userInfo(),
      });
      this.#toast('admin.users.items.acceptanceActDownloaded', 'success');
    } catch {
      // Intake succeeded; act download is best-effort.
    }
  }
}
